import React, { useEffect, useState } from 'react'
import { Checkbox, Col, Flex, Form, Input, Row, Select, Spin, Tooltip, Typography } from 'antd'
import { colorBorderDisabled } from 'src/app/themes/primaryThemeDefault'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import { getCutoffTimeList } from '../add-client-setup/utils'
import { getIssuedCheckFileFormats, getPayeeMatchParameters } from 'src/app/shared/services/ClientSetupService'

interface Props {
  control: any
  isCreateExceptions: boolean
}

function CheckExceptions(props: Props) {
  const [issuedCheckFileFormats, setIssuedCheckFileFormats] = useState<any>([])
  const [fetchingIssuedCheckFile, setIsfetchingIssuedCheckFile] = useState<boolean>(false)
  const { control, isCreateExceptions } = props
  const [payeeMatchParameters, setPayeeMatchParameters] = useState<any>([])
  const [fetchingPayeeMatchParameters, setIsfetchingPayeeMatchParameters] = useState<boolean>(false)

  const getPayeeMatchParametersList = async () => {
    try {
      setIsfetchingPayeeMatchParameters(true)
      const res = await getPayeeMatchParameters()
      if (res?.data?.length) setPayeeMatchParameters(res?.data)
    } finally {
      setIsfetchingPayeeMatchParameters(false)
    }
  }

  const { t } = useTranslation()

  const lookupValues = useSelector(getLookupValues)

  const cutoffTimeList = getCutoffTimeList(15)

  const getIssuedCheckFileFormatsList = async () => {
    try {
      setIsfetchingIssuedCheckFile(true)
      const res = await getIssuedCheckFileFormats()
      if (res?.data?.length) setIssuedCheckFileFormats(res?.data)
    } finally {
      setIsfetchingIssuedCheckFile(false)
    }
  }

  useEffect(() => {
    getIssuedCheckFileFormatsList()
    getPayeeMatchParametersList()
  }, [])

  const { DefaultCheckExceptionCreationType, DefaultDecision, DefaultReason } = lookupValues || {}

  return (
    <Flex vertical style={{ paddingBottom: '24px' }}>
      <Typography.Text style={{ fontSize: '18px', fontWeight: '700', lineHeight: '28px', paddingBottom: '4px' }}>
        {t('ClientSetup:addNewClient.checkExceptions')}
      </Typography.Text>
      <Flex vertical style={{ border: `1px solid ${colorBorderDisabled}`, padding: '16px', borderRadius: '16px' }}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label={t('ClientSetup:addNewClient.label.checkExceptionCreationType')} className='isRequired'>
              <Controller
                name='defaultCheckExceptionCreationType'
                control={control}
                render={({ field }) => <Select {...field} options={DefaultCheckExceptionCreationType} />}
                disabled
              />
            </Form.Item>

            {isCreateExceptions && (
              <Form.Item label={t('ClientSetup:addNewClient.label.checkDecision')} className='isRequired'>
                <Controller
                  name='defaultCheckDecision'
                  control={control}
                  render={({ field }) => <Select {...field} options={DefaultDecision} />}
                  disabled
                />
              </Form.Item>
            )}
          </Col>

          {isCreateExceptions && (
            <>
              <Col span={12}>
                <Form.Item label={t('ClientSetup:addNewClient.label.checkExceptionCutoffTime')} className='isRequired'>
                  <Controller
                    name='defaultCheckExceptionCutoffTime'
                    control={control}
                    render={({ field }) => <Select {...field} options={cutoffTimeList} />}
                    disabled
                  />
                </Form.Item>

                <Form.Item label={t('ClientSetup:addNewClient.label.checkReason')} className='isRequired'>
                  <Controller
                    name='defaultCheckReason'
                    control={control}
                    render={({ field }) => <Select {...field} options={DefaultReason} />}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('ClientSetup:addNewClient.label.issuedCheckFileFormats')}>
                  <Controller
                    name='issuedCheckFileFormats'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        mode='multiple'
                        options={issuedCheckFileFormats?.map((item: any) => ({
                          ...item,
                          value: item?.id,
                          label: item?.name,
                        }))}
                        maxTagCount={'responsive'}
                        maxTagPlaceholder={(omittedValues) => (
                          <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                            <span>+ {omittedValues.length}...</span>
                          </Tooltip>
                        )}
                        notFoundContent={fetchingIssuedCheckFile ? <Spin size='small' /> : null}
                        loading={fetchingIssuedCheckFile}
                      />
                    )}
                    disabled
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        {isCreateExceptions && (
          <>
            <Row gutter={10} align='middle'>
              <Col span={12}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='displayIssuedPayee'
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field?.value}>
                        {t('ClientSetup:addNewClient.label.displayIssuedPayee')}
                      </Checkbox>
                    )}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='payeeMatch'
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field?.value}>
                        {t('ClientSetup:addNewClient.label.payeeMatch')}
                      </Checkbox>
                    )}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Controller
                    name='payeeMatchParameterId'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        defaultValue='Payee Match Parameter'
                        options={payeeMatchParameters?.map((item: any) => ({
                          ...item,
                          value: item?.id,
                          label: item?.name,
                        }))}
                        notFoundContent={fetchingPayeeMatchParameters ? <Spin size='small' /> : null}
                        loading={fetchingPayeeMatchParameters}
                      />
                    )}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10} align='middle'>
              <Col span={12}>
                <Form.Item>
                  <Controller
                    name='displayNotes'
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field?.value}>
                        {t('ClientSetup:addNewClient.label.displayNotes')}
                      </Checkbox>
                    )}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='autoLoadIssuedCheckFiles'
                    control={control}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field?.value}>
                        {t('ClientSetup:addNewClient.label.autoLoadIssuedCheckFiles')}
                      </Checkbox>
                    )}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Controller
                    name='sftpFileLocation'
                    control={control}
                    render={({ field }) => <Input {...field} className='ant-input-view-only' />}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default CheckExceptions
